import { useCallback, useMemo, useState } from 'react'

import { UsersPageViewTab } from '../types'

interface ITabColumnPrefs {
  order: string[]
  hidden: string[]
}

interface IStoredColumnPrefs {
  version: number
  profile?: ITabColumnPrefs
  leaveDetails?: ITabColumnPrefs
}

export interface IUseColumnPrefsParams {
  userId: string
  tab: UsersPageViewTab
  defaultOrder: string[]
  hiddenByDefault: string[]
}

export interface IUseColumnPrefsResult {
  columnOrder: string[]
  columnVisibility: Record<string, boolean>
  visibleColumnOrder: string[]
  setColumnOrder: (order: string[]) => void
  setColumnHidden: (columnId: string, hidden: boolean) => void
  showAll: () => void
  resetToDefault: () => void
}

const storageKey = (userId: string) => `${userId}#usersTableColumns`

function readStored(userId: string): IStoredColumnPrefs {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        return parsed as IStoredColumnPrefs
      }
    }
    return { version: 1 }
  } catch {
    return { version: 1 }
  }
}

function writeStored(userId: string, prefs: IStoredColumnPrefs): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs))
  } catch {
    return
  }
}

function reconcileOrder(storedOrder: string[] | undefined, defaultOrder: string[]): string[] {
  if (!storedOrder || !Array.isArray(storedOrder)) {
    return defaultOrder
  }
  const known = storedOrder.filter((id) => defaultOrder.includes(id))
  const missing = defaultOrder.filter((id) => !known.includes(id))
  return [...known, ...missing]
}

function reconcileHidden(storedHidden: string[] | undefined, defaultOrder: string[], hiddenByDefault: string[]): string[] {
  if (!storedHidden || !Array.isArray(storedHidden)) {
    return hiddenByDefault
  }
  return storedHidden.filter((id) => defaultOrder.includes(id))
}

export function useColumnPrefs(params: IUseColumnPrefsParams): IUseColumnPrefsResult {
  const { userId, tab, defaultOrder, hiddenByDefault } = params
  const [stored, setStored] = useState<ITabColumnPrefs | undefined>(() => readStored(userId)[tab])

  const { columnOrder, columnVisibility, visibleColumnOrder } = useMemo(() => {
    const order = reconcileOrder(stored?.order, defaultOrder)
    const hidden = reconcileHidden(stored?.hidden, defaultOrder, hiddenByDefault)
    const visibility: Record<string, boolean> = {}
    for (const id of hidden) {
      visibility[id] = false
    }
    return { columnOrder: order, columnVisibility: visibility, visibleColumnOrder: order.filter((id) => !hidden.includes(id)) }
  }, [stored, defaultOrder, hiddenByDefault])

  const update = useCallback(
    (updater: (current: ITabColumnPrefs) => ITabColumnPrefs) => {
      const allStored = readStored(userId)
      const current: ITabColumnPrefs = {
        order: reconcileOrder(allStored[tab]?.order, defaultOrder),
        hidden: reconcileHidden(allStored[tab]?.hidden, defaultOrder, hiddenByDefault),
      }
      const next = updater(current)
      writeStored(userId, { ...allStored, version: 1, [tab]: next })
      setStored(next)
    },
    [userId, tab, defaultOrder, hiddenByDefault]
  )

  const setColumnOrder = useCallback(
    (order: string[]) => {
      update((current) => ({ ...current, order }))
    },
    [update]
  )

  const setColumnHidden = useCallback(
    (columnId: string, hidden: boolean) => {
      update((current) => ({
        ...current,
        hidden: hidden ? Array.from(new Set([...current.hidden, columnId])) : current.hidden.filter((id) => id !== columnId),
      }))
    },
    [update]
  )

  const showAll = useCallback(() => {
    update((current) => ({ ...current, hidden: [] }))
  }, [update])

  const resetToDefault = useCallback(() => {
    const allStored = readStored(userId)
    delete allStored[tab]
    writeStored(userId, { ...allStored, version: 1 })
    setStored(undefined)
  }, [userId, tab])

  return { columnOrder, columnVisibility, visibleColumnOrder, setColumnOrder, setColumnHidden, showAll, resetToDefault }
}
